import { useSettings } from '@backpackjs/storefront';
import { useEffect, useState } from 'react';
import { useGlobalProductPageContext } from '../../contexts/globalProductPageContext';
import { useVariantPrices } from '../../hooks';
import { Headline, Link } from '../../snippets';
import { OkendoStarRating } from '../../snippets/Okendo/OkendoStarRating';

export function ProductHeader({
  product,
  selectedVariant,
  purchaseType,
  selectedSellingPlan,
}) {
  const { price, compareAtPrice } = useVariantPrices({
    variant: selectedVariant,
    purchaseType,
    isPdp: true,
  });
  const settings = useSettings();
  const { enabledStarRating } = { ...settings?.product?.reviews };
  const [hasReviews, setHasReviews] = useState(false);

  const { productTestResults, productByline } = useGlobalProductPageContext();

  useEffect(() => {
    const metaFields = product?.metafields;
    metaFields?.forEach((metaField) => {
      if (metaField.key === 'ProductReviewsWidgetSnippet') {
        setHasReviews(true);
      }
    });
  }, [product]);

  return (
    <div className="product-header text-primary md:pl-4 lg:pl-10 xl:pl-16">
      <div
        className="product-header__eyebrow mb-[0.6rem] flex items-center gap-x-2
        md:mb-4 md:flex-col md:items-start md:gap-x-0 md:gap-y-2
        lg:flex-row lg:flex-nowrap lg:items-center lg:gap-x-2 lg:gap-y-0"
      >
        {enabledStarRating && hasReviews && (
          <button
            className="product-header__ratings inline-flex items-center gap-x-1 whitespace-nowrap"
            aria-label="Scroll to product reviews"
            type="button"
          >
            <OkendoStarRating
              className="[&_span]:text-[14px] [&_span]:font-light [&_span]:md:text-[20px]"
              productId={product?.legacyResourceId}
            />
          </button>
        )}

        {productTestResults && (
          <div className="product-header__test-results flex items-center justify-start whitespace-nowrap">
            <span className="mr-2 select-none text-center text-sm font-light opacity-10 md:hidden md:text-xl lg:inline-block">
              |
            </span>
            <Link
              href={productTestResults.url}
              className="text-sm font-light leading-normal underline hover:underline md:text-xl lg:no-underline"
            >
              {productTestResults.text}
            </Link>
          </div>
        )}
      </div>

      <Headline
        element="h1"
        variant="h3"
        className="product-header__title -mt-[5px] mb-[3.2px] text-[22px] md:mb-6 md:mt-0"
      >
        {product.title}
      </Headline>

      {productByline && (
        <div className="product-header__byline tracking-wide mb-[0.2rem] text-sm font-light uppercase md:mb-6">
          {productByline}
        </div>
      )}

      <div className="product-header__price-container -mt-[5px] flex min-h-[1.5rem] items-center gap-2 text-[20px] md:mt-2">
        {compareAtPrice && (
          <div className="product-header__compare-price text-lg text-mediumGray line-through md:text-xl">
            {compareAtPrice}
          </div>
        )}

        <div className="product-header__price text-xl md:mt-0">
          {purchaseType === 'subscription' &&
          selectedSellingPlan?.priceAdjustments
            ? new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency:
                  selectedSellingPlan?.priceAdjustments?.[0]?.price
                    ?.currencyCode,
              }).format(
                selectedSellingPlan?.priceAdjustments?.[0]?.price?.amount
              )
            : price}
        </div>
      </div>
    </div>
  );
}

ProductHeader.displayName = 'ProductHeader';
